<template>
  <div>
    <v-container fluid>
      <base-material-card icon="mdi-robot-happy-outline" title="จัดการผู้ช่วย HRVEC (Chatbot)" class="px-5 py-3">
        <v-alert v-if="setupError" type="warning" outlined class="mt-4">
          {{ setupError }}
          <div class="caption mt-1">
            ต้องรัน <code>MIGRATION_chatbot_log.sql</code> และ <code>MIGRATION_chatbot_admin.sql</code> ก่อนใช้งานหน้านี้
            (แชทยังตอบคำถามจากฐานความรู้ในระบบได้ตามปกติ)
          </div>
        </v-alert>

        <v-tabs v-model="tab" show-arrows class="mt-2">
          <v-tab><v-icon left small>mdi-view-dashboard-outline</v-icon>ภาพรวม</v-tab>
          <v-tab>
            <v-icon left small>mdi-comment-question-outline</v-icon>คำถามรอคำตอบ
            <v-chip v-if="pending.length" x-small color="warning" class="ml-2">{{ pending.length }}</v-chip>
          </v-tab>
          <v-tab><v-icon left small>mdi-book-open-variant</v-icon>คลังคำถาม-คำตอบ</v-tab>
          <v-tab><v-icon left small>mdi-flask-outline</v-icon>ทดสอบถาม-ตอบ</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" class="pt-4">
          <!-- ============ ภาพรวม ============ -->
          <v-tab-item>
            <v-row align="center" class="mb-1">
              <v-col cols="12" sm="4" md="3">
                <v-select
                  v-model="days"
                  :items="dayOptions"
                  label="ช่วงเวลา"
                  dense
                  outlined
                  hide-details
                  @change="loadSummary"
                ></v-select>
              </v-col>
              <v-col cols="auto">
                <v-btn color="primary" outlined :loading="loading.summary" @click="loadSummary">
                  <v-icon left>mdi-refresh</v-icon>รีเฟรช
                </v-btn>
              </v-col>
            </v-row>

            <v-row class="mb-2">
              <v-col v-for="card in totalCards" :key="card.label" cols="6" sm="4" md>
                <v-card :color="card.color" dark :class="{ 'stat-link': card.tab }" @click="card.tab && (tab = card.tab)">
                  <v-card-text class="text-center">
                    <div class="text-h4">{{ card.value }}</div>
                    <div>{{ card.label }}</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <h3 class="mb-2">ประเด็นที่ถามบ่อย</h3>
                <v-data-table
                  :headers="topHeaders"
                  :items="topItems"
                  :items-per-page="10"
                  :loading="loading.summary"
                  dense
                  class="elevation-1"
                  no-data-text="ยังไม่มีข้อมูล"
                ></v-data-table>
              </v-col>
              <v-col cols="12" md="6">
                <h3 class="mb-2">คำตอบที่ผู้ใช้กด "ไม่ช่วย"</h3>
                <v-data-table
                  :headers="negativeHeaders"
                  :items="negativeItems"
                  :items-per-page="10"
                  :loading="loading.summary"
                  dense
                  class="elevation-1"
                  no-data-text="ยังไม่มีข้อมูล"
                >
                  <template v-slot:item.created_at="{ item }">{{ formatDate(item.created_at) }}</template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      v-if="item.intent"
                      x-small
                      text
                      color="primary"
                      title="ปรับปรุงคำตอบนี้"
                      @click="openEditor(item.intent, [])"
                    >
                      <v-icon x-small left>mdi-pencil</v-icon>ปรับปรุง
                    </v-btn>
                  </template>
                </v-data-table>
              </v-col>
              <v-col cols="12" md="4">
                <h3 class="mb-2">กลุ่มผู้ใช้ที่ถาม</h3>
                <v-simple-table dense class="elevation-1">
                  <tbody>
                    <tr v-for="row in summary.roles" :key="row.user_role">
                      <td>{{ roleLabel(row.user_role) }}</td>
                      <td class="text-right">{{ row.cnt }}</td>
                    </tr>
                    <tr v-if="!summary.roles.length">
                      <td class="grey--text">ยังไม่มีข้อมูล</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- ============ คำถามรอคำตอบ ============ -->
          <v-tab-item>
            <v-alert type="info" text dense>
              คำถามที่ผู้ใช้พิมพ์แล้วผู้ช่วย<strong>ตอบไม่ได้</strong> จัดกลุ่มข้อความเดียวกันไว้ด้วยกัน —
              เลือก <strong>ตอบใหม่</strong> (สร้างหัวข้อใหม่), <strong>เพิ่มเข้าหัวข้อเดิม</strong> (ใช้คำตอบที่มีอยู่ + เพิ่มคำสำคัญ)
              หรือ <strong>ไม่ต้องตอบ</strong> (ข้อความไม่เกี่ยวกับระบบ) เมื่อจัดการแล้วรายการจะหายไปจากหน้านี้
            </v-alert>

            <v-row align="center" dense class="mb-2">
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  v-model="pendingSearch"
                  prepend-inner-icon="mdi-magnify"
                  label="ค้นหาคำถาม"
                  dense
                  outlined
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="auto">
                <v-btn outlined color="primary" :loading="loading.pending" @click="loadPending">
                  <v-icon left>mdi-refresh</v-icon>รีเฟรช
                </v-btn>
              </v-col>
              <v-spacer></v-spacer>
              <v-col v-if="selectedPending.length" cols="auto">
                <span class="caption mr-2">เลือก {{ selectedPending.length }} รายการ</span>
                <v-btn small color="primary" class="mr-1" @click="openEditor(null, selectedQuestions)">
                  <v-icon small left>mdi-plus</v-icon>ตอบรวมเป็นหัวข้อใหม่
                </v-btn>
                <v-btn small color="primary" outlined class="mr-1" @click="openAttach(selectedQuestions)">
                  <v-icon small left>mdi-link-variant</v-icon>เพิ่มเข้าหัวข้อเดิม
                </v-btn>
                <v-btn small outlined @click="ignore(selectedQuestions)">
                  <v-icon small left>mdi-eye-off-outline</v-icon>ไม่ต้องตอบ
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              v-model="selectedPending"
              :headers="pendingHeaders"
              :items="pendingItems"
              :search="pendingSearch"
              :loading="loading.pending"
              item-key="question"
              show-select
              :items-per-page="15"
              class="elevation-1"
              no-data-text="ไม่มีคำถามรอคำตอบ 🎉"
            >
              <template v-slot:item.question="{ item }">
                <span class="font-weight-medium">{{ item.question }}</span>
                <div class="caption grey--text">หน้า {{ item.last_page || "-" }}</div>
              </template>
              <template v-slot:item.roles="{ item }">
                <v-chip v-for="r in item.roleList" :key="r" x-small class="mr-1 mb-1">{{ roleShort(r) }}</v-chip>
              </template>
              <template v-slot:item.last_asked="{ item }">{{ formatDate(item.last_asked) }}</template>
              <template v-slot:item.suggestion="{ item }">
                <a v-if="item.suggestion" class="caption" @click="openEditor(item.suggestion, [item.question])">
                  {{ item.suggestion.title }}
                </a>
                <span v-else class="caption grey--text">-</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <div class="text-no-wrap">
                  <v-btn x-small color="primary" depressed class="mr-1" @click="openEditor(null, [item.question])">
                    ตอบใหม่
                  </v-btn>
                  <v-btn x-small color="primary" outlined class="mr-1" @click="openAttach([item.question], item.suggestion)">
                    เพิ่มเข้าหัวข้อเดิม
                  </v-btn>
                  <v-btn x-small text @click="ignore([item.question])">ไม่ต้องตอบ</v-btn>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ============ คลังคำถาม-คำตอบ ============ -->
          <v-tab-item>
            <v-row align="center" dense class="mb-2">
              <v-col cols="12" sm="4" md="3">
                <v-text-field
                  v-model="librarySearch"
                  prepend-inner-icon="mdi-magnify"
                  label="ค้นหาหัวข้อ / คำสำคัญ / คำตอบ"
                  dense
                  outlined
                  hide-details
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="6" sm="3" md="2">
                <v-select
                  v-model="libraryCategory"
                  :items="[{ text: 'ทุกหมวด', value: '' }, ...categoryItems]"
                  label="หมวด"
                  dense
                  outlined
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="6" sm="3" md="2">
                <v-select
                  v-model="librarySource"
                  :items="sourceOptions"
                  label="ที่มา/สถานะ"
                  dense
                  outlined
                  hide-details
                ></v-select>
              </v-col>
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <v-btn color="primary" :disabled="!!setupError" @click="openEditor(null, [])">
                  <v-icon left>mdi-plus</v-icon>เพิ่มคำถาม-คำตอบ
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="libraryHeaders"
              :items="libraryItems"
              :loading="loading.rows"
              item-key="id"
              :items-per-page="15"
              class="elevation-1"
              no-data-text="ไม่พบหัวข้อ"
            >
              <template v-slot:item.title="{ item }">
                <v-icon v-if="item.pinned" small color="amber darken-2" title="ปักหมุด">mdi-pin</v-icon>
                <span :class="{ 'grey--text': !item.is_active }">{{ item.title }}</span>
                <div class="caption grey--text text-truncate library-answer">{{ item.answer }}</div>
              </template>
              <template v-slot:item.category="{ item }">{{ categoryLabel(item.category) }}</template>
              <template v-slot:item.source="{ item }">
                <v-chip x-small :color="sourceChip(item).color" dark>{{ sourceChip(item).text }}</v-chip>
              </template>
              <template v-slot:item.roles="{ item }">
                <span v-if="!item.roles" class="caption grey--text">ทุกกลุ่ม</span>
                <v-chip v-for="r in item.roles || []" :key="r" x-small class="mr-1 mb-1">{{ roleShort(r) }}</v-chip>
              </template>
              <template v-slot:item.keywords="{ item }">{{ item.keywords.length }}</template>
              <template v-slot:item.is_active="{ item }">
                <v-switch
                  :input-value="item.is_active"
                  :disabled="!!setupError || item.id === 'greeting'"
                  :loading="busyId === item.id"
                  inset
                  dense
                  hide-details
                  color="success"
                  class="mt-0"
                  @change="setActive(item, $event)"
                ></v-switch>
              </template>
              <template v-slot:item.actions="{ item }">
                <div class="text-no-wrap">
                  <v-btn icon small color="primary" title="แก้ไข" :disabled="!!setupError" @click="openEditor(item, [])">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small title="ทดสอบ" @click="testWith(item)">
                    <v-icon small>mdi-flask-outline</v-icon>
                  </v-btn>
                  <v-btn v-if="item.source === 'custom'" icon small color="error" title="ลบ" @click="remove(item)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="item.source === 'override'"
                    icon
                    small
                    color="warning"
                    title="ยกเลิกการแก้ไข กลับไปใช้คำตอบเดิมของระบบ"
                    @click="remove(item)"
                  >
                    <v-icon small>mdi-backup-restore</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- ============ ทดสอบถาม-ตอบ ============ -->
          <v-tab-item>
            <v-row>
              <v-col cols="12" md="5">
                <v-alert type="info" text dense>
                  จำลองการถามในฐานะผู้ใช้แต่ละกลุ่ม ใช้ฐานความรู้ล่าสุด (รวมรายการที่เพิ่งบันทึก) —
                  การทดสอบในหน้านี้ไม่ถูกบันทึกเป็นสถิติ
                </v-alert>
                <v-select v-model="testRole" :items="roleItems" label="ถามในฐานะ" dense outlined></v-select>
                <v-text-field
                  v-model="testQuestion"
                  label="พิมพ์คำถามแบบผู้ใช้"
                  append-icon="mdi-send"
                  outlined
                  clearable
                  @keyup.enter="runTest"
                  @click:append="runTest"
                ></v-text-field>
                <div class="caption grey--text mb-1">ตัวอย่างคำถามรอคำตอบ:</div>
                <v-chip
                  v-for="p in pending.slice(0, 6)"
                  :key="p.question"
                  small
                  outlined
                  class="mr-1 mb-1"
                  @click="testQuestion = p.question; runTest()"
                >
                  {{ p.question }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="7">
                <template v-if="testResult">
                  <div class="d-flex align-center mb-3">
                    <v-chip small :color="confidenceChip.color" dark class="mr-2">{{ confidenceChip.text }}</v-chip>
                    <span class="caption grey--text">คะแนน {{ testResult.score.toFixed(2) }}</span>
                    <v-spacer></v-spacer>
                    <v-btn v-if="testResult.intent" x-small text color="primary" @click="openEditor(libraryOf(testResult.intent), [testedQuestion])">
                      <v-icon x-small left>mdi-pencil</v-icon>แก้ไขหัวข้อนี้
                    </v-btn>
                    <v-btn x-small text color="primary" @click="openEditor(null, [testedQuestion])">
                      <v-icon x-small left>mdi-plus</v-icon>สร้างคำตอบใหม่จากคำถามนี้
                    </v-btn>
                  </div>
                  <div class="preview-surface">
                    <chat-answer-preview v-if="testResult.intent" :intent="testResult.intent" :role="testRole"></chat-answer-preview>
                    <div v-else class="grey--text">ผู้ช่วยจะตอบว่า "ยังไม่เข้าใจคำถาม" และเสนอหัวข้อใกล้เคียงให้เลือก</div>
                  </div>
                  <div v-if="testResult.alternatives.length" class="mt-3">
                    <div class="caption grey--text mb-1">
                      {{ testResult.intent ? "หัวข้อใกล้เคียงที่แสดงเพิ่ม" : "หัวข้อที่เสนอให้เลือก" }}
                    </div>
                    <v-chip v-for="alt in testResult.alternatives" :key="alt.id" small outlined color="primary" class="mr-1 mb-1">
                      {{ alt.title }}
                    </v-chip>
                  </div>
                </template>
                <div v-else class="grey--text text-center pa-8">
                  <v-icon large color="grey lighten-1">mdi-chat-processing-outline</v-icon>
                  <div>พิมพ์คำถามเพื่อดูว่าผู้ช่วยจะตอบอย่างไร</div>
                </div>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </base-material-card>
    </v-container>

    <!-- เลือกหัวข้อเดิมเพื่อเพิ่มคำถาม -->
    <v-dialog v-model="attach.open" max-width="560">
      <v-card>
        <v-card-title>เพิ่มคำถามเข้าหัวข้อที่มีอยู่</v-card-title>
        <v-card-text>
          <div class="mb-3">
            <v-chip v-for="q in attach.questions" :key="q" small class="mr-1 mb-1">{{ q }}</v-chip>
          </div>
          <v-autocomplete
            v-model="attach.intentId"
            :items="attachItems"
            label="เลือกหัวข้อที่ใช้ตอบ"
            outlined
            dense
            autofocus
          ></v-autocomplete>
          <div class="caption grey--text">
            ขั้นตอนถัดไปจะเปิดฟอร์มแก้ไขหัวข้อนั้น พร้อมเพิ่มคำถามข้างต้นเป็นคำสำคัญให้แล้ว ตรวจทานแล้วกดบันทึก
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="attach.open = false">ยกเลิก</v-btn>
          <v-btn color="primary" :disabled="!attach.intentId" @click="confirmAttach">ถัดไป</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <chatbot-faq-editor
      v-model="editor.open"
      :intent="editor.intent"
      :source-questions="editor.questions"
      :route-items="routeItems"
      @saved="onSaved"
    ></chatbot-faq-editor>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3500" bottom left>
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
/**
 * chatbot_manage.vue — จัดการผู้ช่วย HRVEC สำหรับผู้ดูแลระบบส่วนกลาง (แทนหน้า chatbot_insights เดิม)
 *  - ภาพรวม: สถิติจาก chatbot_log.summary.php
 *  - คำถามรอคำตอบ: คำถามที่ผู้ช่วยตอบไม่ได้ → ตอบใหม่ / เพิ่มเข้าหัวข้อเดิม / ไม่ต้องตอบ
 *  - คลังคำถาม-คำตอบ: หัวข้อในระบบ (knowledgeBase.js) + ที่แอดมินเพิ่ม (chatbot_faq) — แก้ไข/ปิด/ปักหมุดได้
 *  - ทดสอบ: จำลองถามในฐานะกลุ่มผู้ใช้ต่างๆ
 * ข้อมูลที่แก้ไขมีผลกับแชทของผู้ใช้ทันทีที่เปิดหน้าเว็บใหม่ (chatbot_faq.php cache 5 นาที)
 */
import { CATEGORIES, ROLE_LABELS } from "@/chatbot/knowledgeBase";
import { getIntent, libraryIntents, matchQuestion, setCustomIntents } from "@/chatbot/chatEngine";
import ChatAnswerPreview from "@/chatbot/admin/ChatAnswerPreview.vue";
import ChatbotFaqEditor from "@/chatbot/admin/ChatbotFaqEditor.vue";

const ROLE_SHORT = { guest: "ทั่วไป", teacher: "ครู", gov_em: "พนง.ราชการ", etc: "ลูกจ้าง", A: "A", B: "B", C: "C", G: "G", S: "S" };
const SOURCE_CHIPS = {
  builtin: { text: "ในระบบ", color: "blue-grey" },
  override: { text: "ในระบบ (แก้ไขแล้ว)", color: "indigo" },
  custom: { text: "เพิ่มโดยแอดมิน", color: "teal" }
};

export default {
  name: "ChatbotManage",
  components: { ChatAnswerPreview, ChatbotFaqEditor },
  data() {
    return {
      tab: 0,
      days: 30,
      dayOptions: [
        { text: "7 วันล่าสุด", value: 7 },
        { text: "30 วันล่าสุด", value: 30 },
        { text: "90 วันล่าสุด", value: 90 },
        { text: "1 ปี", value: 365 }
      ],
      loading: { summary: false, pending: false, rows: false },
      setupError: "",
      summary: { totals: {}, top: [], negative: [], roles: [] },
      pending: [],
      pendingSearch: "",
      selectedPending: [],
      rows: [],
      librarySearch: "",
      libraryCategory: "",
      librarySource: "",
      busyId: null,
      editor: { open: false, intent: null, questions: [] },
      attach: { open: false, questions: [], intentId: null },
      testRole: "guest",
      testQuestion: "",
      testedQuestion: "",
      testResult: null,
      snackbar: { show: false, text: "", color: "success" },
      categoryItems: CATEGORIES.map(c => ({ text: c.label, value: c.id })),
      roleItems: Object.keys(ROLE_LABELS).map(k => ({ text: ROLE_LABELS[k], value: k })),
      sourceOptions: [
        { text: "ทั้งหมด", value: "" },
        { text: "มากับระบบ", value: "builtin" },
        { text: "มากับระบบ (แก้ไขแล้ว)", value: "override" },
        { text: "เพิ่มโดยแอดมิน", value: "custom" },
        { text: "ปิดใช้งานอยู่", value: "inactive" },
        { text: "ปักหมุด", value: "pinned" }
      ],
      topHeaders: [
        { text: "ประเด็น", value: "title" },
        { text: "จำนวน", value: "cnt", align: "end", width: 80 },
        { text: "👍", value: "helpful", align: "end", width: 60 },
        { text: "👎", value: "not_helpful", align: "end", width: 60 }
      ],
      negativeHeaders: [
        { text: "คำถาม", value: "question" },
        { text: "คำตอบที่ให้", value: "title" },
        { text: "เวลา", value: "created_at", width: 120 },
        { text: "", value: "actions", sortable: false, width: 90 }
      ],
      pendingHeaders: [
        { text: "คำถามจากผู้ใช้", value: "question" },
        { text: "จำนวน", value: "cnt", align: "end", width: 80 },
        { text: "กลุ่มผู้ถาม", value: "roles", sortable: false, width: 140 },
        { text: "ถามล่าสุด", value: "last_asked", width: 130 },
        { text: "หัวข้อที่อาจใช้ตอบได้", value: "suggestion", sortable: false },
        { text: "จัดการ", value: "actions", sortable: false, width: 260 }
      ],
      libraryHeaders: [
        { text: "หัวข้อ", value: "title" },
        { text: "หมวด", value: "category", width: 120 },
        { text: "ที่มา", value: "source", width: 150 },
        { text: "กลุ่มผู้ใช้", value: "roles", sortable: false, width: 150 },
        { text: "คำสำคัญ", value: "keywords", align: "end", width: 90 },
        { text: "เปิดใช้", value: "is_active", width: 80 },
        { text: "", value: "actions", sortable: false, width: 120 }
      ]
    };
  },
  computed: {
    library() {
      return libraryIntents(this.rows);
    },
    libraryItems() {
      const q = (this.librarySearch || "").trim().toLowerCase();
      return this.library.filter(it => {
        if (this.libraryCategory && it.category !== this.libraryCategory) return false;
        if (this.librarySource === "inactive" && it.is_active) return false;
        if (this.librarySource === "pinned" && !it.pinned) return false;
        if (["builtin", "override", "custom"].includes(this.librarySource) && it.source !== this.librarySource) return false;
        if (!q) return true;
        return [it.title, it.answer, ...(it.keywords || [])].some(t => String(t || "").toLowerCase().includes(q));
      });
    },
    attachItems() {
      return this.library
        .filter(it => it.is_active && !["greeting", "thanks"].includes(it.id))
        .map(it => ({ text: `${it.title} (${this.categoryLabel(it.category)})`, value: it.id }));
    },
    pendingItems() {
      return this.pending.map(p => {
        const match = matchQuestion(p.question, "guest");
        const suggestion = match.intent || match.alternatives[0] || null;
        return {
          ...p,
          roleList: (p.roles || "").split(",").filter(Boolean),
          suggestion: suggestion ? this.libraryOf(suggestion) : null
        };
      });
    },
    selectedQuestions() {
      return this.selectedPending.map(p => p.question);
    },
    totalCards() {
      const t = this.summary.totals || {};
      return [
        { label: "คำถามทั้งหมด", value: t.total || 0, color: "primary" },
        { label: "รอคำตอบ", value: this.pending.length, color: "warning", tab: 1 },
        { label: "ช่วยได้ 👍", value: t.helpful || 0, color: "success" },
        { label: "ไม่ช่วย 👎", value: t.not_helpful || 0, color: "error" },
        { label: "ถูกเตือนข้อมูลส่วนบุคคล", value: t.pii_blocked || 0, color: "blue-grey" }
      ];
    },
    topItems() {
      return this.summary.top.map(row => ({ ...row, title: this.intentTitle(row.intent_id) }));
    },
    negativeItems() {
      return this.summary.negative.map(row => ({
        ...row,
        title: row.intent_id ? this.intentTitle(row.intent_id) : "-",
        intent: this.library.find(it => it.id === row.intent_id) || null
      }));
    },
    // หน้าจอในระบบสำหรับปุ่มลิงก์ — ตัดหน้าพิมพ์/หน้าที่ต้องมีพารามิเตอร์ออก
    routeItems() {
      const items = [];
      const walk = (routes, parent) => {
        routes.forEach(r => {
          const path = r.path.startsWith("/") ? r.path : `${parent.replace(/\/$/, "")}/${r.path}`;
          if (r.path && !path.includes(":") && !/print|pdf/i.test(path) && /^\/[A-Za-z0-9_/-]*$/.test(path)) {
            items.push({ text: `${r.name || path} — ${path}`, value: path });
          }
          if (r.children) walk(r.children, path);
        });
      };
      walk(this.$router.options.routes, "/");
      const seen = new Set();
      return items.filter(i => !seen.has(i.value) && seen.add(i.value));
    },
    confidenceChip() {
      const map = {
        high: { text: "ตอบได้ (มั่นใจสูง)", color: "success" },
        medium: { text: "ตอบได้ + เสนอทางเลือก", color: "info" },
        none: { text: "ตอบไม่ได้", color: "error" }
      };
      return map[this.testResult.confidence];
    }
  },
  mounted() {
    this.loadRows();
    this.loadSummary();
    this.loadPending();
  },
  methods: {
    async post(payload, file = "chatbot_faq.manage.php") {
      try {
        const res = await this.$http.post(file, payload);
        if (res.data && res.data.status) return res.data;
        this.notify((res.data && res.data.message) || "ดำเนินการไม่สำเร็จ", "error");
      } catch (e) {
        if (e.isSessionExpired) return null;
        const message = (e.response && e.response.data && e.response.data.message) || "เชื่อมต่อเซิร์ฟเวอร์ไม่สำเร็จ";
        if (e.response && e.response.status === 500) this.setupError = message;
        else this.notify(message, "error");
      }
      return null;
    },
    async loadRows() {
      this.loading.rows = true;
      const data = await this.post({ action: "list" });
      if (data) {
        this.rows = data.items;
        // ให้แชทในแท็บนี้ (และหน้าอื่นที่ยังเปิดอยู่) ใช้ข้อมูลล่าสุดทันที
        setCustomIntents(this.rows);
      }
      this.loading.rows = false;
    },
    async loadSummary() {
      this.loading.summary = true;
      const data = await this.post({ days: this.days }, "chatbot_log.summary.php");
      if (data) this.summary = data;
      this.loading.summary = false;
    },
    async loadPending() {
      this.loading.pending = true;
      const data = await this.post({ action: "pending", days: 365 });
      if (data) this.pending = data.items;
      this.selectedPending = [];
      this.loading.pending = false;
    },
    libraryOf(intent) {
      return this.library.find(it => it.id === intent.id) || intent;
    },
    openEditor(intent, questions) {
      this.editor = { open: true, intent: intent ? this.libraryOf(intent) : null, questions: [...questions] };
    },
    openAttach(questions, suggestion = null) {
      this.attach = { open: true, questions: [...questions], intentId: suggestion ? suggestion.id : null };
    },
    confirmAttach() {
      const intent = this.library.find(it => it.id === this.attach.intentId);
      this.attach.open = false;
      if (intent) this.openEditor(intent, this.attach.questions);
    },
    async onSaved(result) {
      this.notify(
        result.resolved ? `บันทึกแล้ว และจัดการคำถามรอคำตอบ ${result.resolved} รายการ` : "บันทึกเรียบร้อย"
      );
      await Promise.all([this.loadRows(), this.loadPending()]);
      if (this.testResult) this.runTest();
    },
    async ignore(questions) {
      if (!questions.length) return;
      if (!confirm(`ทำเครื่องหมาย "ไม่ต้องตอบ" ${questions.length} คำถาม?`)) return;
      const data = await this.post({ action: "resolve", status: "ignored", questions });
      if (data) {
        this.notify(`ซ่อน ${data.resolved} รายการแล้ว`);
        this.loadPending();
      }
    },
    async setActive(item, active) {
      this.busyId = item.id;
      let data;
      if (item.faqId) {
        data = await this.post({ action: "toggle", id: item.faqId, is_active: active });
      } else {
        // หัวข้อในระบบที่ยังไม่เคยแก้ → บันทึกเป็นแถวแก้ไขทับที่มีเนื้อหาเดิม แต่ปิดใช้งาน
        data = await this.post({
          action: "save",
          intent_key: item.id,
          title: item.title,
          category: item.category,
          keywords: item.keywords,
          answer: item.answer,
          steps: item.steps || [],
          tip: item.tip || "",
          links: item.links || [],
          roles: item.roles || [],
          pinned: !!item.pinned,
          is_active: active
        });
      }
      if (data) this.notify(active ? "เปิดใช้งานแล้ว" : "ปิดใช้งานแล้ว — ผู้ช่วยจะไม่ใช้หัวข้อนี้ตอบ");
      await this.loadRows();
      this.busyId = null;
    },
    async remove(item) {
      const message =
        item.source === "custom"
          ? `ลบหัวข้อ "${item.title}" ถาวร?`
          : `ยกเลิกการแก้ไข "${item.title}" และกลับไปใช้คำตอบเดิมของระบบ?`;
      if (!confirm(message)) return;
      const data = await this.post({ action: "delete", id: item.faqId });
      if (data) this.notify(item.source === "custom" ? "ลบแล้ว" : "กลับไปใช้คำตอบเดิมของระบบแล้ว");
      this.loadRows();
    },
    testWith(item) {
      this.tab = 3;
      this.testQuestion = item.keywords[0] || item.title;
      this.testRole = (item.roles && item.roles[0]) || "guest";
      this.runTest();
    },
    runTest() {
      const question = (this.testQuestion || "").trim();
      if (!question) return;
      this.testedQuestion = question;
      this.testResult = matchQuestion(question, this.testRole);
    },
    intentTitle(id) {
      const intent = getIntent(id) || this.library.find(it => it.id === id);
      return intent ? intent.title : `${id} (ถูกลบแล้ว)`;
    },
    categoryLabel(id) {
      const cat = CATEGORIES.find(c => c.id === id);
      return cat ? cat.label : id;
    },
    sourceChip(item) {
      if (!item.is_active) return { text: `${SOURCE_CHIPS[item.source].text} · ปิด`, color: "grey" };
      return SOURCE_CHIPS[item.source];
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || role;
    },
    roleShort(role) {
      return ROLE_SHORT[role] || role;
    },
    formatDate(value) {
      return value ? this.$moment(value).add(543, "years").format("D MMM YYYY HH:mm") : "";
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    }
  }
};
</script>

<style scoped>
.preview-surface {
  padding: 16px;
  background: #f5f7fb;
  border-radius: 12px;
}
.library-answer {
  max-width: 420px;
}
.stat-link {
  cursor: pointer;
}
</style>
